import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import XeroLeaveSettings from './XeroLeaveSettings';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import xeroPayrollService, {
  type XeroPayrollCalendar,
  type XeroEmployeeMatching,
  type XeroEmployee,
} from '../../services/xeroPayroll';
import { AlertTriangle, CheckCircle2, ChevronRight, ListChecks, RefreshCw, Users, XCircle } from 'lucide-react';

/**
 * What payroll does with the organisation's Xero connection: pay calendars,
 * earnings rates, matching people to Xero employees, and leave.
 *
 * It holds no sign-in of its own. The page above it (`/accounts/xero`)
 * connects Xero once for payroll and invoicing together and hands this the
 * result; nothing here works until a Xero organisation is chosen there.
 *
 * Any accounts seat reads it. `canEdit` (the Accounts Admin seat) changes it:
 * everything inside is in one disabled fieldset otherwise, and the server
 * refuses regardless.
 */

const errorMessage = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;

const employeeLabel = (e: XeroEmployee) => {
  const name = `${e.first_name} ${e.last_name}`.trim() || '(no name)';
  return e.status === 'TERMINATED' ? `${name} (terminated)` : name;
};

const userLabel = (u: { first_name: string | null; last_name: string | null; username: string }) =>
  `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;

interface Props {
  /** A Xero organisation is chosen and the connection holds payroll's permissions. */
  ready: boolean;
  /** The chosen Xero organisation; a change reloads what was read from it. */
  tenantId: string | null;
  /** False when the grant can read payroll but not send to it. */
  canPost: boolean;
  /** Accounts Admin: may change what is here. */
  canEdit: boolean;
}

export default function XeroPayrollPanel({ ready, tenantId, canPost, canEdit }: Props) {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const [info, setInfo] = useState<string | null>(null);
  const [calendars, setCalendars] = useState<XeroPayrollCalendar[] | null>(null);

  const [matching, setMatching] = useState<XeroEmployeeMatching | null>(null);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingError, setMatchingError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<number, string | null>>({});
  const [savingLinks, setSavingLinks] = useState(false);
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);

  const applyMatching = useCallback((data: XeroEmployeeMatching) => {
    setMatching(data);
    setDraft(Object.fromEntries(data.users.map(u => [u.user_id, u.xero_employee_id])));
  }, []);

  const loadMatching = useCallback(async () => {
    setMatchingLoading(true);
    setMatchingError(null);
    try {
      const res = await xeroPayrollService.getEmployeeMatching();
      if (res.success && res.data) applyMatching(res.data);
    } catch (e: unknown) {
      // An organisation that does not run its payroll in Xero lands here: the
      // connection is fine for invoices, and Xero says why payroll is not.
      setMatchingError(errorMessage(e, 'Could not read employees from Xero.'));
    } finally {
      setMatchingLoading(false);
    }
  }, [applyMatching]);

  const loadCalendars = useCallback(async () => {
    try {
      const res = await xeroPayrollService.listPayrollCalendars();
      if (res.success && res.data) setCalendars(res.data);
    } catch {
      setCalendars(null);
    }
  }, []);

  useEffect(() => {
    if (!ready) {
      setMatching(null);
      setCalendars(null);
      return;
    }
    void loadMatching();
    void loadCalendars();
  }, [ready, tenantId, loadMatching, loadCalendars]);

  // Active employees first, then terminated — a leaver is still pickable for
  // back pay, but should not be the first thing offered.
  const employeeOptions = useMemo(() => {
    if (!matching) return [];
    return [...matching.employees].sort((a, b) => {
      const at = a.status === 'TERMINATED' ? 1 : 0;
      const bt = b.status === 'TERMINATED' ? 1 : 0;
      return at - bt || employeeLabel(a).localeCompare(employeeLabel(b));
    });
  }, [matching]);

  const employeeById = useMemo(
    () => new Map((matching?.employees ?? []).map(e => [e.xero_employee_id, e])),
    [matching]
  );

  // Xero employees picked for more than one person in the draft.
  const duplicated = useMemo(() => {
    const seen = new Map<string, number>();
    for (const id of Object.values(draft)) if (id) seen.set(id, (seen.get(id) ?? 0) + 1);
    return new Set([...seen].filter(([, n]) => n > 1).map(([id]) => id));
  }, [draft]);

  const changed = useMemo(
    () => (matching?.users ?? []).filter(u => (draft[u.user_id] ?? null) !== u.xero_employee_id),
    [matching, draft]
  );

  const pendingSuggestions = useMemo(
    () => (matching?.users ?? []).filter(u =>
      !u.xero_employee_id && u.suggested_xero_employee_id && !draft[u.user_id]),
    [matching, draft]
  );

  const acceptSuggestions = () => {
    setDraft(prev => {
      const next = { ...prev };
      for (const u of pendingSuggestions) next[u.user_id] = u.suggested_xero_employee_id;
      return next;
    });
  };

  const saveLinks = async () => {
    setMatchingError(null);
    setInfo(null);
    setSavingLinks(true);
    try {
      const res = await xeroPayrollService.saveEmployeeLinks(
        changed.map(u => ({ user_id: u.user_id, xero_employee_id: draft[u.user_id] ?? null }))
      );
      if (res.success && res.data) {
        applyMatching(res.data);
        setInfo(`Saved ${changed.length} employee link${changed.length === 1 ? '' : 's'}.`);
      }
    } catch (e: unknown) {
      setMatchingError(errorMessage(e, 'Could not save employee links.'));
    } finally {
      setSavingLinks(false);
    }
  };

  const linkedCount = matching?.users.filter(u => u.xero_employee_id && !u.link_stale).length ?? 0;
  const visibleUsers = (matching?.users ?? []).filter(u => !onlyUnlinked || !draft[u.user_id] || u.link_stale);

  return (
    <fieldset disabled={!canEdit} className="space-y-4 min-w-0 border-0 p-0 m-0">
      {info && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2">
          <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">{info}</span>
        </div>
      )}

      {ready && !canPost && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">
            This connection can only read payroll from Xero. To send timesheets and leave, reconnect Xero above and
            approve the timesheet and employee permissions on Xero&rsquo;s screen. Your settings and employee links
            are kept.
          </span>
        </div>
      )}

      {ready && calendars && calendars.length > 0 && (
        <div className="p-3 bg-white border border-neutral-200 rounded-xl text-sm text-neutral-700 break-words">
          <span className="font-medium text-neutral-900">Pay calendars in Xero: </span>
          {calendars.map(c =>
            `${c.name}${c.start_date ? ` (from ${formatLocalDateInTz(c.start_date, orgTz)})` : ''}`
          ).join(', ')}
        </div>
      )}

      {/* Earnings rates */}
      <button
        type="button"
        onClick={() => navigate('/accounts/payroll-mappings')}
        disabled={!ready}
        className="w-full flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="bg-emerald-100 p-3 rounded-lg shrink-0">
            <ListChecks className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-neutral-900 break-words">Earnings rates</h3>
            <p className="text-sm text-neutral-500 break-words">
              Map each payroll category to one of your Xero earnings rates, picked from a list.
            </p>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
      </button>

      {/* Employees */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary-600 shrink-0" />
                Match employees
              </h3>
              <p className="text-sm text-neutral-500 mt-1 break-words">
                Link each person to their Xero employee record. Suggestions match on email first,
                then on full name, and only when there is exactly one candidate. Nothing is linked
                until you save.
              </p>
            </div>
            {matching && (
              <span className="text-sm text-neutral-600 shrink-0">
                {linkedCount} of {matching.users.length} linked
              </span>
            )}
          </div>

          {!ready ? (
            <p className="text-sm text-neutral-500 break-words">Connect Xero and choose its organisation above first.</p>
          ) : matchingLoading && !matching ? (
            <p className="text-sm text-neutral-500">Reading employees from Xero…</p>
          ) : (
            <>
              {matchingError && (
                <p className="mb-3 text-sm text-red-600 break-words flex items-start gap-2">
                  <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span className="min-w-0">{matchingError}</span>
                </p>
              )}
              {matching && (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Button
                      variant="outline"
                      onClick={acceptSuggestions}
                      disabled={pendingSuggestions.length === 0}
                    >
                      Accept {pendingSuggestions.length} suggestion{pendingSuggestions.length === 1 ? '' : 's'}
                    </Button>
                    <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        checked={onlyUnlinked}
                        onChange={e => setOnlyUnlinked(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      Only show unlinked
                    </label>
                    <Button variant="ghost" onClick={() => void loadMatching()} disabled={matchingLoading} className="flex items-center gap-1">
                      <RefreshCw className="h-4 w-4" />
                      Reload from Xero
                    </Button>
                  </div>

                  <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg">
                    {visibleUsers.map(u => {
                      const value = draft[u.user_id] ?? '';
                      const suggestion = u.suggested_xero_employee_id
                        ? employeeById.get(u.suggested_xero_employee_id)
                        : undefined;
                      const isDup = !!value && duplicated.has(value);
                      const stale = u.link_stale && value === u.xero_employee_id;
                      return (
                        <li key={u.user_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <div className="sm:w-2/5 min-w-0">
                            <p className="font-medium text-neutral-900 break-words">{userLabel(u)}</p>
                            {u.email && <p className="text-xs text-neutral-500 break-all">{u.email}</p>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <select
                              aria-label={`Xero employee for ${userLabel(u)}`}
                              value={value}
                              onChange={e => setDraft(prev => ({ ...prev, [u.user_id]: e.target.value || null }))}
                              className={`w-full border rounded-md px-2 py-1.5 text-sm bg-white ${
                                isDup || stale ? 'border-red-400 text-red-700' : 'border-neutral-300 text-neutral-900'
                              }`}
                            >
                              <option value="">— Not linked —</option>
                              {stale && (
                                <option value={u.xero_employee_id ?? ''}>
                                  {u.xero_employee_name || 'Unknown'} — no longer in Xero
                                </option>
                              )}
                              {employeeOptions.map(e => (
                                <option key={e.xero_employee_id} value={e.xero_employee_id}>
                                  {employeeLabel(e)}{e.email ? ` · ${e.email}` : ''}
                                </option>
                              ))}
                            </select>
                            {isDup && (
                              <p className="mt-1 text-xs text-red-600">Chosen for more than one person.</p>
                            )}
                            {stale && (
                              <p className="mt-1 text-xs text-red-600">This employee no longer exists in Xero. Pick their current record.</p>
                            )}
                            {!value && suggestion && (
                              <p className="mt-1 text-xs text-amber-700 break-words">
                                Suggested: {employeeLabel(suggestion)} (same {u.suggestion_reason === 'email' ? 'email' : 'name'})
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                    {visibleUsers.length === 0 && (
                      <li className="p-3 text-sm text-neutral-500">Everyone is linked.</li>
                    )}
                  </ul>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Button onClick={saveLinks} disabled={savingLinks || changed.length === 0 || duplicated.size > 0}>
                      {savingLinks
                        ? 'Saving…'
                        : changed.length === 0
                          ? 'No changes'
                          : `Save ${changed.length} change${changed.length === 1 ? '' : 's'}`}
                    </Button>
                    {changed.length > 0 && (
                      <Button variant="outline" onClick={() => matching && applyMatching(matching)} disabled={savingLinks}>
                        Discard changes
                      </Button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <XeroLeaveSettings enabled={ready} />
    </fieldset>
  );
}
